/**
 * Sales Order React Query Hooks
 *
 * Provides data fetching and mutation hooks for sales orders
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  SalesOrder,
  CreateSalesOrderDto,
  UpdateSalesOrderDto,
  SalesOrderKpis,
  SalesOrderStatus,
  OrderLineItem,
} from '@lognai/plugin-orders';
import {
  listSalesOrders,
  getSalesOrdersByCustomer,
  getSalesOrder,
  createSalesOrder,
  updateSalesOrder,
  updateSalesOrderStatus,
  confirmSalesOrder,
  cancelSalesOrder,
  fulfillSalesOrder,
  getSalesOrderLines,
  getSalesOrderStockStatus,
  getSalesOrderKpis,
  type SalesOrderFilterParams,
  type PaginatedSalesOrderList,
  type SalesOrderStockStatus,
} from '../api/sales-orders';
import { salesOrderKeys } from './query-keys';

/**
 * Hook to fetch paginated sales orders
 */
export const useSalesOrders = (
  params?: SalesOrderFilterParams,
  options?: Omit<UseQueryOptions<PaginatedSalesOrderList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: salesOrderKeys.list(params),
    queryFn: () => listSalesOrders(params),
    ...options,
  });
};

/**
 * Hook to fetch sales orders by customer
 */
export const useSalesOrdersByCustomer = (
  customerId: string,
  params?: SalesOrderFilterParams,
  options?: Omit<UseQueryOptions<PaginatedSalesOrderList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: salesOrderKeys.byCustomer(customerId, params),
    queryFn: () => getSalesOrdersByCustomer(customerId, params),
    enabled: !!customerId,
    ...options,
  });
};

/**
 * Hook to fetch a single sales order
 */
export const useSalesOrder = (
  id: string,
  options?: Omit<UseQueryOptions<SalesOrder>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: salesOrderKeys.detail(id),
    queryFn: () => getSalesOrder(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Hook to fetch sales order line items
 */
export const useSalesOrderLines = (
  id: string,
  options?: Omit<UseQueryOptions<OrderLineItem[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: salesOrderKeys.lines(id),
    queryFn: () => getSalesOrderLines(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Hook to fetch stock status for sales order
 */
export const useSalesOrderStockStatus = (
  id: string,
  options?: Omit<UseQueryOptions<SalesOrderStockStatus>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: salesOrderKeys.stockStatus(id),
    queryFn: () => getSalesOrderStockStatus(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Hook to fetch sales order KPIs
 */
export const useSalesOrderKpis = (
  options?: Omit<UseQueryOptions<SalesOrderKpis>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: salesOrderKeys.kpis(),
    queryFn: getSalesOrderKpis,
    ...options,
  });
};

/**
 * Hook to create a sales order
 */
export const useCreateSalesOrder = (
  options?: Omit<UseMutationOptions<SalesOrder, Error, CreateSalesOrderDto>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSalesOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.kpis() });
    },
    ...options,
  });
};

/**
 * Hook to update a sales order
 */
export const useUpdateSalesOrder = (
  options?: Omit<
    UseMutationOptions<SalesOrder, Error, { id: string; dto: UpdateSalesOrderDto }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }) => updateSalesOrder(id, dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.detail(data.id) });
      // Line items are now editable, so the cached lines + derived stock status
      // must be refreshed or the detail page keeps showing the pre-edit rows.
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lines(data.id) });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.stockStatus(data.id) });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.kpis() });
    },
    ...options,
  });
};

/**
 * Hook to update sales order status
 */
export const useUpdateSalesOrderStatus = (
  options?: Omit<
    UseMutationOptions<
      SalesOrder,
      Error,
      { id: string; status: SalesOrderStatus; reason?: string }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, reason }) => updateSalesOrderStatus(id, status, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.kpis() });
    },
    ...options,
  });
};

/**
 * Hook to confirm a sales order
 */
export const useConfirmSalesOrder = (
  options?: Omit<
    UseMutationOptions<
      SalesOrder,
      Error,
      { id: string; promisedDeliveryDate?: string; notes?: string }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, promisedDeliveryDate, notes }) =>
      confirmSalesOrder(id, promisedDeliveryDate, notes),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.kpis() });
    },
    ...options,
  });
};

/**
 * Hook to cancel a sales order
 */
export const useCancelSalesOrder = (
  options?: Omit<
    UseMutationOptions<SalesOrder, Error, { id: string; reason?: string }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => cancelSalesOrder(id, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.kpis() });
    },
    ...options,
  });
};

/**
 * Hook to fulfill a sales order
 */
export const useFulfillSalesOrder = (
  options?: Omit<
    UseMutationOptions<
      SalesOrder,
      Error,
      {
        id: string;
        items?: Array<{
          lineItemId: string;
          quantity: number;
          warehouseId?: string;
          locationId?: string;
        }>;
        shipmentId?: string;
        notes?: string;
      }
    >,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, items, shipmentId, notes }) =>
      fulfillSalesOrder(id, items, shipmentId, notes),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.kpis() });
      queryClient.invalidateQueries({ queryKey: salesOrderKeys.stockStatus(data.id) });
    },
    ...options,
  });
};
